import React, { useState, useCallback } from 'react'
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material'
import _debounce from 'lodash/debounce'

import { useRequisitionSearchQuery } from 'services/hooks/requisition/useRequisitionSearchQuery'
import { FilterSelect } from 'components/listTableFilterBox'
import { useRequisitionAssignUsersQuery } from 'services/hooks/requisition/useRequisitionAssignUsersQuery'
import { useRequisitionStatusListQuery } from 'services/hooks/requisition/useRequisitionStatusListQuery'

const DEBOUNCE_TIME_MS = 1000

type AutocompleteSearchProps = {
  onChangeFilter: (target: string, value: string) => void
  filterValues: {
    statuses: string[]
    users: string[]
    term: string
  }
}

const Filters = ({ onChangeFilter, filterValues }: AutocompleteSearchProps) => {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [term, setTerm] = useState('')
  const { data: requisitionAssignUsersList } = useRequisitionAssignUsersQuery()
  const { data: statusList } = useRequisitionStatusListQuery()
  const { data: requisitionSearchResult, isFetching } =
    useRequisitionSearchQuery({
      term,
      assignee: filterValues.users.join(),
      status: filterValues.statuses.join(),
    })

  const debouncedUpdateData = useCallback(
    _debounce((value: string) => {
      setTerm(value)
    }, DEBOUNCE_TIME_MS),
    []
  )

  const onChangeSearchField = async (e) => {
    setSearchValue(e.target.value)
    debouncedUpdateData(e.target.value)
  }

  const onChangeValue = (event, value) => {
    onChangeFilter('term', value)
  }

  return (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      <Autocomplete
        id='asynchronous-demo'
        style={{ width: 200 }}
        open={open}
        onOpen={() => {
          setOpen(true)
        }}
        onClose={() => {
          setOpen(false)
        }}
        options={requisitionSearchResult?.search.terms || []}
        loading={isFetching}
        onChange={onChangeValue}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Search'
            variant='outlined'
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isFetching ? (
                    <CircularProgress color='inherit' size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            value={searchValue}
            onChange={onChangeSearchField}
          />
        )}
      />
      <Typography>Filter By:</Typography>
      {requisitionAssignUsersList && (
        <FilterSelect
          label='Assignee'
          filterType='users'
          filterList={requisitionAssignUsersList}
          selectValues={filterValues.users}
          onChange={onChangeFilter}
        />
      )}
      {statusList && (
        <FilterSelect
          label='Status'
          filterType='statuses'
          filterList={statusList}
          selectValues={filterValues.statuses}
          onChange={onChangeFilter}
        />
      )}
    </Box>
  )
}

export default Filters
